import React from "react";
import {
  FILTER_CLEAR_ALL,
  FILTER_DONE,
  FILTER_FACETS,
  FILTER_PANEL_TITLE,
  filterResultText,
} from "../Commend3Constants";
import { Commend3FacetPicks } from "../Commend3Facets";

type ModelFilterPanelProps = {
  isOpen: boolean;
  picks: Commend3FacetPicks;
  openFacets: Record<string, boolean>;
  totalPicks: number;
  resultCount: number;
  onToggleFacet: (key: string) => void;
  onPick: (facetKey: string, value: string) => void;
  onClearAll: () => void;
  onClose: () => void;
};

const svgBase = {
  fill: "none",
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" className="tw-h-[19px] tw-w-[19px]" style={{ ...svgBase, stroke: "currentColor", strokeWidth: 1.8 }} aria-hidden="true">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

const ChevronIcon = () => (
  <svg viewBox="0 0 24 24" className="tw-h-5 tw-w-5" style={{ ...svgBase, stroke: "#0B0C23", strokeWidth: 1.6 }} aria-hidden="true">
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" className="tw-h-3 tw-w-3" style={{ ...svgBase, stroke: "#fff", strokeWidth: 3 }} aria-hidden="true">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

const slugify = (value: string): string =>
  value.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

const ModelFilterPanel = ({
  isOpen,
  picks,
  openFacets,
  totalPicks,
  resultCount,
  onToggleFacet,
  onPick,
  onClearAll,
  onClose,
}: ModelFilterPanelProps) => {
  if (!isOpen) return null;

  return (
    <>
      <div
        id="commend3-filter-overlay"
        className="tw-fixed tw-inset-0 tw-z-[170] tw-animate-fadeIn"
        style={{ background: "rgba(11,12,35,.18)" }}
        onClick={onClose}
      />
      <div
        id="commend3-filter-panel"
        className="tw-animate-slideIn tw-fixed tw-right-0 tw-top-0 tw-z-[171] tw-flex tw-h-screen tw-w-[420px] tw-max-w-[96vw] tw-flex-col tw-bg-white"
        style={{
          borderLeft: "1px solid #E3E5EC",
          boxShadow: "-4px 0 20px rgba(3,5,98,.10)",
        }}
        role="dialog"
        aria-label={FILTER_PANEL_TITLE}
      >
        <div className="tw-flex tw-shrink-0 tw-items-center tw-justify-between tw-gap-3 tw-px-[26px] tw-pb-[18px] tw-pt-[22px]">
          <span
            className="tw-text-[22px] tw-font-semibold tw-text-[#0B0C23]"
            style={{ letterSpacing: "-0.02em" }}
          >
            {FILTER_PANEL_TITLE}
          </span>
          <button
            id="commend3-filter-close"
            type="button"
            className="tw-flex tw-h-[30px] tw-w-[30px] tw-items-center tw-justify-center tw-rounded-md tw-text-[#0B0C23]"
            onClick={onClose}
            aria-label="Close filter"
          >
            <CloseIcon />
          </button>
        </div>

        <div className="tw-flex-1 tw-overflow-y-auto tw-px-[26px] tw-pb-5">
          {FILTER_FACETS.map((facet) => {
            const selected = picks[facet.key] ?? [];
            const open = Boolean(openFacets[facet.key]);

            return (
              <div key={facet.key} style={{ borderBottom: "1px solid #F0F1F6" }}>
                <div
                  id={`commend3-facet-${facet.key}`}
                  className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2.5 tw-py-[17px]"
                  onClick={() => onToggleFacet(facet.key)}
                >
                  <span className="tw-min-w-0 tw-flex-1 tw-text-[17px] tw-font-semibold tw-text-[#0B0C23]">
                    {facet.label}
                  </span>
                  {selected.length > 0 ? (
                    <span className="tw-inline-flex tw-h-5 tw-min-w-[22px] tw-items-center tw-justify-center tw-rounded-full tw-bg-[#EAF0FF] tw-px-[7px] tw-text-[11.5px] tw-font-bold tw-text-[#030562]">
                      {selected.length}
                    </span>
                  ) : null}
                  <span
                    className="tw-inline-flex tw-shrink-0"
                    style={{
                      transition: "transform 150ms ease",
                      transform: open ? "rotate(180deg)" : "rotate(0deg)",
                    }}
                  >
                    <ChevronIcon />
                  </span>
                </div>

                {open ? (
                  <div className="tw-flex tw-flex-col tw-gap-0.5 tw-pb-[14px]">
                    {facet.options.map((option) => {
                      const on = selected.includes(option.value);
                      return (
                        <div
                          key={option.value}
                          id={`commend3-facet-${facet.key}-${slugify(option.value)}`}
                          className="tw-flex tw-cursor-pointer tw-items-center tw-gap-[11px] tw-rounded-md tw-px-1 tw-py-2"
                          onClick={() => onPick(facet.key, option.value)}
                        >
                          <span
                            className="tw-inline-flex tw-h-[18px] tw-w-[18px] tw-shrink-0 tw-items-center tw-justify-center tw-rounded"
                            style={{
                              border: `1.5px solid ${on ? "#030562" : "#D6D9E2"}`,
                              background: on ? "#030562" : "#fff",
                            }}
                          >
                            {on ? <CheckIcon /> : null}
                          </span>
                          <span className="tw-min-w-0 tw-flex-1 tw-text-[14.5px] tw-text-[#0B0C23]">
                            {option.label}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>

        <div
          className="tw-flex tw-shrink-0 tw-items-center tw-gap-3 tw-px-[26px] tw-py-4"
          style={{ borderTop: "1px solid #E3E5EC", background: "#FAFBFD" }}
        >
          <span
            id="commend3-filter-clear"
            className="tw-text-[14px] tw-font-medium"
            style={{
              color: totalPicks ? "#0066A0" : "#B4B9D4",
              cursor: totalPicks ? "pointer" : "default",
            }}
            onClick={totalPicks ? onClearAll : undefined}
          >
            {FILTER_CLEAR_ALL}
          </span>
          <div className="tw-flex-1" />
          <span className="tw-text-[13.5px] tw-text-[#59596E]">
            {filterResultText(resultCount)}
          </span>
          <button
            id="commend3-filter-done"
            type="button"
            className="tw-rounded-md tw-bg-[#030562] tw-px-[22px] tw-py-2.5 tw-text-[15px] tw-font-medium tw-text-white"
            onClick={onClose}
          >
            {FILTER_DONE}
          </button>
        </div>
      </div>
    </>
  );
};

export default ModelFilterPanel;
